// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import StationQueue from './StationQueue'

/**
 * All prescriptions — every state in the Rx lifecycle.
 *
 * This is the view a pharmacy sees when the queues module / custom views are
 * off (PharmacyLayout collapses the Prescriptions page to this single "All"
 * list), and the audit-style "see everything" tab otherwise.
 *
 * ALL_RX_STATES below MUST mirror the dbo.rx_items CK_rx_state constraint
 * (db/migrations/068_rx_state_active_expired.sql) — the source of truth. Any
 * persisted state missing here silently vanishes from this view. That was the
 * bug this fixes: 'active' (filled, refills remaining — ongoing specialty
 * therapy) and 'expired' (closed: out of refills / past expiration) were
 * omitted, so PioneerRx specialty fills — which map to exactly those two
 * states — were invisible here. 'draft' = PioneerRx "waiting for data entry".
 *
 * KNOWN RELATED DRIFT (handle separately, not in this PR): the backend
 * canonical list RxStateMachine.ClinicalStates (exposed via
 * GET /api/workflow/rx-states) is ALSO stale — it still lists only 10 states
 * and is missing 'active'/'expired'. That backend reconciliation has wider
 * reach (transition validation, dashboards) and needs its own change.
 *
 * StationQueue's search bar + sortable columns keep this usable at scale;
 * default sort is last_state_change_at desc.
 *
 * 'cancelled' is deliberately EXCLUDED from this view (ClickUp 86cb22vg0):
 * cancelled prescriptions are noise in the working list. They are fully
 * captured by the canned "Cancelled prescriptions" report (/reports/rx-cancelled),
 * which is the audit path for them.
 */
const ALL_RX_STATES = [
  'draft', 'submitted', 'verified', 'ready_to_fill', 'dispensed',
  'out_for_delivery', 'delivered', 'on_hold', 'rejected',
  'active', 'expired',
].join(',')

export default function AllRxTab() {
  return (
    <StationQueue
      states={ALL_RX_STATES}
      tabKey="all"
      title="All prescriptions"
      subtitle="Every prescription across all queues. Use the search and column sorting to find what you need."
      emptyMessage="No prescriptions found."
      customColumns
    />
  )
}
